import { useState } from 'react';
import { ExternalLink } from 'lucide-react';

import { EnvironmentId } from '@/react/portainer/environments/types';

import { DateRangeTimePicker } from '@@/DateRangeTimePicker/DateRangeTimePicker';
import { InformationPanel } from '@@/InformationPanel';
import { Alert } from '@@/Alert';
import { InlineLoader } from '@@/InlineLoader';
import { Widget } from '@@/Widget';

import {
  EnvironmentObservabilityResponse,
  useEnvironmentObservability,
} from '../../queries/useEnvironmentObservability';
import {
  MetricName,
  useResourceMetrics,
} from '../../queries/useResourceMetrics';

import { DEFAULT_SERIES_COLOR, MetricCard } from './MetricCard';

const NETWORK_TRANSMIT_COLOR = '#e34948';

interface Props {
  environmentId: EnvironmentId;
  namespace?: string;
  /** Workload or pod kind to scope metrics to, e.g. Deployment, StatefulSet, DaemonSet, Pod. Must be paired with `name`. */
  kind?: string;
  /** Workload or pod name to scope metrics to. Must be paired with `kind`. */
  name?: string;
}

const ONE_DAY_S = 24 * 60 * 60;
const DEFAULT_AGGREGATION = 'Avg';

export function ObservabilityMetrics({
  environmentId,
  namespace = '',
  kind,
  name,
}: Props) {
  const obsQuery = useEnvironmentObservability(environmentId);
  const [to, setTo] = useState(() => Math.floor(Date.now() / 1000));
  const [from, setFrom] = useState(() => to - ONE_DAY_S);

  const metricsEnabled = Boolean(obsQuery.data?.enabled);
  const oneUptimeUrl = obsQuery.data
    ? buildOneUptimeUrl(obsQuery.data, namespace, kind, name)
    : undefined;

  if (obsQuery.isLoading) {
    return <InlineLoader>Checking observability status...</InlineLoader>;
  }

  if (obsQuery.isError) {
    return (
      <Alert color="error" title="Failed to load observability status">
        Unable to determine if observability is enabled for this environment.
      </Alert>
    );
  }

  if (!obsQuery.data?.enabled) {
    return (
      <InformationPanel>
        Observability is not configured for this environment. Enable it in the
        environment settings to view metrics here.
      </InformationPanel>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Widget className="mx-4">
        <Widget.Title title="Usage and availability" />
        <Widget.Body>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-end gap-3">
              <div className="w-fit">
                <DateRangeTimePicker
                  since={from}
                  until={to}
                  onSinceChange={setFrom}
                  onUntilChange={setTo}
                />
              </div>
              {oneUptimeUrl && (
                <OneUptimeLink
                  href={oneUptimeUrl}
                  label="Open in OneUptime"
                  dataCy="observability-metrics-open-oneuptime"
                />
              )}
            </div>

            <MetricCardQuery
              title="Heartbeats over selected time range"
              unit="percent"
              metric="heartbeat"
              environmentId={environmentId}
              namespace={namespace}
              kind={kind}
              name={name}
              from={from}
              to={to}
              enabled={metricsEnabled}
            />
          </div>
        </Widget.Body>
      </Widget>

      <div className="mx-4 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <Widget>
          <Widget.Title title="CPU metrics" />
          <Widget.Body>
            <MetricCardQuery
              unit="percent"
              metric="cpu"
              environmentId={environmentId}
              namespace={namespace}
              kind={kind}
              name={name}
              from={from}
              to={to}
              enabled={metricsEnabled}
            />
          </Widget.Body>
        </Widget>

        <Widget>
          <Widget.Title title="Memory metrics" />
          <Widget.Body>
            <MetricCardQuery
              unit="bytes"
              metric="memory"
              environmentId={environmentId}
              namespace={namespace}
              kind={kind}
              name={name}
              from={from}
              to={to}
              enabled={metricsEnabled}
            />
          </Widget.Body>
        </Widget>

        <Widget>
          <Widget.Title title="Network metrics" />
          <Widget.Body>
            <NetworkMetricCard
              environmentId={environmentId}
              namespace={namespace}
              kind={kind}
              name={name}
              from={from}
              to={to}
              enabled={metricsEnabled}
            />
          </Widget.Body>
        </Widget>

        <Widget>
          <Widget.Title title="File System metrics" />
          <Widget.Body>
            <MetricCardQuery
              unit="bytes"
              metric="filesystem"
              environmentId={environmentId}
              namespace={namespace}
              kind={kind}
              name={name}
              from={from}
              to={to}
              enabled={metricsEnabled}
            />
          </Widget.Body>
        </Widget>
      </div>
    </div>
  );
}

interface ScopedMetricParams {
  environmentId: EnvironmentId;
  namespace: string;
  kind?: string;
  name?: string;
  from: number;
  to: number;
  enabled: boolean;
}

function MetricCardQuery({
  title,
  unit,
  metric,
  environmentId,
  namespace,
  kind,
  name,
  from,
  to,
  enabled,
}: ScopedMetricParams & {
  title?: string;
  unit: 'percent' | 'bytes';
  metric: MetricName;
}) {
  const query = useResourceMetrics(
    environmentId,
    {
      namespace,
      kind,
      name,
      metric,
      aggregation: DEFAULT_AGGREGATION,
      from,
      to,
    },
    { enabled }
  );

  return (
    <MetricCard
      title={title}
      unit={unit}
      isLoading={query.isLoading && query.isFetching}
      isError={query.isError}
      series={[
        { name: 'value', color: DEFAULT_SERIES_COLOR, data: query.data ?? [] },
      ]}
    />
  );
}

// KIND_URL_SEGMENT maps a workload kind to its OneUptime dashboard URL
// segment (https://github.com/OneUptime/oneuptime — App/FeatureSet/Dashboard/src/Utils/RouteMap.ts).
const KIND_URL_SEGMENT: Record<string, string> = {
  Deployment: 'deployments',
  StatefulSet: 'statefulsets',
  DaemonSet: 'daemonsets',
  Pod: 'pods',
};

// buildOneUptimeUrl builds a deep link into OneUptime's Kubernetes dashboard
// for the namespace or workload currently in view, falling back to no link
// (rather than the project root) when any required ingredient is missing —
// e.g. the environment's cluster is not yet registered in OneUptime.
function buildOneUptimeUrl(
  data: EnvironmentObservabilityResponse,
  namespace: string,
  kind?: string,
  name?: string
): string | undefined {
  if (!data.url || !data.projectId || !data.clusterId) {
    return undefined;
  }

  const base = `${data.url}/dashboard/${data.projectId}/kubernetes/${data.clusterId}`;

  const kindSegment = kind ? KIND_URL_SEGMENT[kind] : undefined;
  if (kindSegment && name) {
    return `${base}/${kindSegment}/${encodeURIComponent(name)}`;
  }

  if (namespace) {
    return `${base}/namespaces/${encodeURIComponent(namespace)}`;
  }

  return undefined;
}

function OneUptimeLink({
  href,
  label,
  dataCy,
}: {
  href: string;
  label: string;
  dataCy: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-cy={dataCy}
      className="flex shrink-0 items-center gap-1 text-xs text-blue-8 hover:text-blue-9 th-highcontrast:text-blue-4 th-highcontrast:hover:text-blue-3 th-dark:text-blue-4 th-dark:hover:text-blue-3"
    >
      {label}
      <ExternalLink size={12} />
    </a>
  );
}

// NetworkMetricCard combines Receive and Transmit into one chart with a
// legend, rather than two separate cards — they're the same metric split by
// direction, best read together on a shared axis.
function NetworkMetricCard({
  environmentId,
  namespace,
  kind,
  name,
  from,
  to,
  enabled,
}: ScopedMetricParams) {
  const receive = useResourceMetrics(
    environmentId,
    {
      namespace,
      kind,
      name,
      metric: 'network_rx',
      aggregation: DEFAULT_AGGREGATION,
      from,
      to,
    },
    { enabled }
  );
  const transmit = useResourceMetrics(
    environmentId,
    {
      namespace,
      kind,
      name,
      metric: 'network_tx',
      aggregation: DEFAULT_AGGREGATION,
      from,
      to,
    },
    { enabled }
  );

  return (
    <MetricCard
      unit="bytes"
      isLoading={
        (receive.isLoading && receive.isFetching) ||
        (transmit.isLoading && transmit.isFetching)
      }
      isError={receive.isError || transmit.isError}
      series={[
        {
          name: 'Receive',
          color: DEFAULT_SERIES_COLOR,
          data: receive.data ?? [],
        },
        {
          name: 'Transmit',
          color: NETWORK_TRANSMIT_COLOR,
          data: transmit.data ?? [],
        },
      ]}
    />
  );
}
